import { streamingResponseText } from "../../i18n/streamingFeedbackPresentation";
import { getCsrfToken } from "../csrf";
import { toast } from "../toast";
import type { SessionActionResult } from "../../hooks/useWSDSessions";

export interface SessionActionSpec {
  sessionId: string;
  /** Route suffix after `/api/streaming/sessions/:id/`, also reported as `result.action`. */
  action: "message" | "stop" | "kill" | "pause" | "resume";
  body: Record<string, unknown>;
  successText: string;
  failureText: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * POST a session action and toast its outcome. The session routes answer
 * `{ ok: true, data: SessionActionResult }` even when the media server refused
 * the action, so the inner `data.success === false` is a failure too: reading
 * only the outer envelope reports "stopped" for a session that keeps playing.
 */
export async function runSessionAction(spec: SessionActionSpec): Promise<SessionActionResult> {
  const { sessionId, action, body, successText, failureText } = spec;
  const failed = (error: string): SessionActionResult => {
    toast.error(error);
    return { success: false, sessionId, action, error };
  };

  try {
    const csrfToken = getCsrfToken();
    const res = await fetch(`/api/streaming/sessions/${encodeURIComponent(sessionId)}/${action}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      },
      body: JSON.stringify(body),
    });
    const data: unknown = await res.json();
    const envelope = isRecord(data) ? data : {};
    const inner = isRecord(envelope.data) ? envelope.data : null;

    if (inner?.success === false) {
      const reason = typeof inner.error === "string" && inner.error ? inner.error : failureText;
      return failed(streamingResponseText(inner, reason));
    }
    if (!res.ok || !envelope.ok) {
      const reason = typeof envelope.message === "string" ? envelope.message : failureText;
      return failed(streamingResponseText(data, reason));
    }

    toast.success(successText);
    return { success: true, sessionId, action, message: successText };
  } catch {
    return failed(failureText);
  }
}
